import React, { type JSX } from 'react';
import { Card, Col, Container, ListGroup, Row, Stack } from 'react-bootstrap';
import { clsx, DisplayUnknownError, StandardButton } from '@soulhx/ui-common';
import { TargetDateStatuses, calculateTargetDateStatus } from './helpers/helper-functions.js';
import { useTranslation } from 'react-i18next';
import {
  addCollapsedPrayerList,
  db,
  deleteCollapsedPrayerList,
  fetchListItemPage,
  getCollapsedPrayerLists,
  type LocalPrayerListItem,
  queueLocalPrayerListItemUpdate,
  queueLocalPrayerUpdate,
  useAsyncLiveQuery,
  usePrayerById,
  useUserSetting,
} from '@soulhx/client-store';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SHXEditor } from '../common/SHXEDitor.js';
import { StyledButton } from './helpers/StyledButton.js';

// how many list items a snapshot card shows before linking to the full list
const SNAPSHOT_LIST_SIZE = 5;

interface ISnapshotCard {
  id: string;
  addErrorMessage: (message: string | JSX.Element) => void;
}
export const SnapshotCard = ({ id, addErrorMessage }: ISnapshotCard) => {
  const { t } = useTranslation(['pray']);
  const collapsedPrayerLists = useSelector(getCollapsedPrayerLists);
  const dispatch = useDispatch();
  const { settingValue: sortOrder } = useUserSetting<string>('prayerSort');
  const navigate = useNavigate();
  const { prayer: item } = usePrayerById(id);

  // live, like the snapshot itself - one extra row tells us whether to link
  // to the full list
  const { data: listPage } = useAsyncLiveQuery(
    async () => {
      const page = await fetchListItemPage(id, undefined, {
        sortOrder,
        showCompleted: false,
        limit: SNAPSHOT_LIST_SIZE + 1,
      });
      const rows = await db.prayerListItems.bulkGet(page.map((row) => row.id));
      return rows.filter((row): row is LocalPrayerListItem => row !== undefined);
    },
    [id, sortOrder],
    []
  );
  const listItems = listPage.slice(0, SNAPSHOT_LIST_SIZE);
  const hasMoreListItems = listPage.length > SNAPSHOT_LIST_SIZE;

  // still loading, deleted, or a list with nothing left to pray through
  if (!item || (item.isList && listItems.length < 1)) return null;

  const collapsePrayerList = collapsedPrayerLists.indexOf(item.id) >= 0;

  const dateStatus = calculateTargetDateStatus(item.targetDate);

  return (
    <Card
      className={clsx('my-1', 'mx-0', 'mx-md-1', 'shadow', {
        'bg-warning-subtle': dateStatus === TargetDateStatuses.yellow,
        'bg-danger-subtle': dateStatus === TargetDateStatuses.red,
        'text-dark': dateStatus === TargetDateStatuses.yellow || dateStatus === TargetDateStatuses.red,
        'link-dark': dateStatus === TargetDateStatuses.yellow || dateStatus === TargetDateStatuses.red,
      })}
    >
      <Card.Text as="div">
        <Container fluid>
          <Row>
            <Col xs="12">
              <Stack direction="horizontal" gap={1}>
                {!item.isList && (
                  <StyledButton
                    rpVariant={
                      dateStatus === TargetDateStatuses.red
                        ? 'overdue'
                        : dateStatus === TargetDateStatuses.yellow
                          ? 'imminent'
                          : undefined
                    }
                    type="complete"
                    done={item.completed}
                    onClick={async () => {
                      try {
                        await queueLocalPrayerUpdate(item.id, { completed: !item.completed });
                      } catch (err) {
                        addErrorMessage(<DisplayUnknownError er={err} fallback={t('pray:errorCompleting')} />);
                      }
                    }}
                  />
                )}
                <SHXEditor key={item.updateDate} initialMarkdown={item.text} readOnly />
                {item.isList && (
                  <StandardButton
                    type="showHide"
                    className="ms-auto"
                    done={!collapsePrayerList}
                    onClick={() => {
                      if (collapsePrayerList) {
                        dispatch(deleteCollapsedPrayerList(item.id));
                      } else {
                        dispatch(addCollapsedPrayerList(item.id));
                      }
                    }}
                  />
                )}
              </Stack>
            </Col>
          </Row>
          {item.isList && !collapsePrayerList && (
            <ListGroup variant="flush">
              {listItems!.map((listItem) => {
                const dateStatus = calculateTargetDateStatus(listItem.targetDate);

                return (
                  <ListGroup.Item
                    variant={
                      dateStatus === TargetDateStatuses.red
                        ? 'danger'
                        : dateStatus === TargetDateStatuses.yellow
                          ? 'warning'
                          : undefined
                    }
                    className={clsx('')}
                    key={`snapshot-${listItem.id}`}
                  >
                    <Stack direction="horizontal" gap={1}>
                      <StyledButton
                        rpVariant={
                          dateStatus === TargetDateStatuses.red
                            ? 'overdue'
                            : dateStatus === TargetDateStatuses.yellow
                              ? 'imminent'
                              : undefined
                        }
                        type="complete"
                        itemType={t('pray:listItemType')}
                        done={listItem.completed}
                        onClick={async () => {
                          try {
                            await queueLocalPrayerListItemUpdate(listItem.id, { completed: !listItem.completed });
                          } catch (err) {
                            addErrorMessage(<DisplayUnknownError er={err} fallback={t('pray:errorCompleting')} />);
                          }
                        }}
                      />

                      <SHXEditor
                        key={listItem.updateDate}
                        initialMarkdown={listItem.itemText}
                        readOnly
                        disableMaxHeight
                      />
                    </Stack>
                  </ListGroup.Item>
                );
              })}
              {hasMoreListItems && (
                <ListGroup.Item variant="secondary" action onClick={() => navigate(`/pray/${item.id}`)}>
                  {t('pray:seeFullList')}
                </ListGroup.Item>
              )}
            </ListGroup>
          )}
        </Container>
      </Card.Text>
    </Card>
  );
};
